import classNames from "classnames";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Button } from "../../components/buttons/button";
import { FileUploadButton } from "../../components/buttons/fileUploadButton";
import { ProfileIcon } from "../../components/cards/profilePic";
import { EventImage } from "../../components/event/eventImage";
import { Icon } from "../../components/icon/icon";
import { SwitchInput } from "../../components/inputs/switch";
import { TextInput } from "../../components/inputs/textInput";
import { Pill } from "../../components/pill";
import { ErrorText } from "../../components/typography/errorText";
import { MiniHeader } from "../../components/typography/miniHeader";
import { BgWrapper } from "../../components/wrapper/bgWrapper";
import { useFileUpload } from "../../customeHooks/useFileUpload";
import { useUpdateProfileMutation } from "../../redux/features/auth/auth.api";
import { useAppSelector } from "../../redux/hooks";
import { MAX_SIZE_MB } from "../../utils/constants";
import { OrganizationData } from "../../utils/types";
import {
  OrganisationInformationSchema,
  SettingPageUpdateProfileSchema,
} from "../../utils/validations";

export const OrganizationForm = ({
  isOrg = false,
  handleDataChange = () => {},
  imageClassName = "!max-h-[80px] min-w-[80px] !max-w-[80px] md:!min-h-[150px] md:!max-h-[150px] md:!min-w-[150px] md:!max-w-[150px] !pt-0 !h-full",
  imageContainerClassName = "",
}: {
  isOrg: boolean;
  handleDataChange: (data: OrganizationData, isValid: boolean) => void;
  imageClassName?: string;
  imageContainerClassName?: string;
}) => {
  const { profile } = useAppSelector((state) => state.auth);

  const {
    uploading,
    handleSingleFileUpload,
    error: imageUploadError,
    uploadedFile,
  } = useFileUpload();

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    isValid,
    setFieldValue,
  } = useFormik({
    initialValues: {
      phoneNumber: profile?.organisation?.phoneNumber ?? "",
      name: profile?.organisation?.name ?? "",
      email: profile?.organisation?.email ?? "",
      logo: profile?.organisation?.logo ?? "",
      isOrganisation: isOrg,
    },
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: OrganisationInformationSchema,
    onSubmit: async (vals) => {
      console.log(vals);
    },
  });

  useEffect(() => {
    handleDataChange(values, isValid);
    // eslint-disable-next-line
  }, [values, isValid]);

  return (
    <div className="flex flex-col gap-4">
      <div
        className={classNames(
          "flex flex-row items-start gap-4",
          imageContainerClassName,
        )}
      >
        <EventImage
          image={values.logo}
          className={classNames(imageClassName)}
        />

        <div className="h-fit flex-1 text-sm">
          {imageUploadError && (
            <ErrorText text={imageUploadError} className="!test-sm" />
          )}

          {!imageUploadError && (
            <p className="line-clamp-2 max-w-full text-ellipsis">
              {uploadedFile
                ? uploadedFile.name
                : "Choose file to upload as organization logo"}
            </p>
          )}

          <p className="mt-[3px] text-grey-500">
            Recommended size 1,000 x 1,000 px, Max file size{" "}
            {MAX_SIZE_MB / (1024 * 1024)}MB
          </p>

          <FileUploadButton
            type="button"
            variation="plain-grey"
            className="mt-4 !max-w-fit"
            onUpload={(uploaded) => {
              handleSingleFileUpload(uploaded, (url) => {
                setFieldValue("logo", url);
              });
            }}
            text={values.logo ? "Replace Logo" : "Upload Logo"}
            icon="upload"
            disabled={uploading}
            loading={uploading}
          />
        </div>
      </div>

      <TextInput
        type="text"
        name="name"
        value={values.name}
        placeholder="Enter organization name"
        onChange={handleChange}
        onBlur={handleBlur}
        hasTitle
        title="Organization Name"
        errors={errors.name && touched.name ? errors.name : ""}
        containerClassName="col-span-full"
      />

      <TextInput
        type="text"
        name="email"
        value={values.email}
        placeholder="Enter email"
        onChange={handleChange}
        onBlur={handleBlur}
        hasTitle
        title="Organization Email"
        errors={errors.email && touched.email ? errors.email : ""}
        containerClassName="col-span-full"
      />

      <TextInput
        type="text"
        name="phoneNumber"
        value={values.phoneNumber}
        placeholder="Enter organization phone number"
        onChange={handleChange}
        onBlur={handleBlur}
        hasTitle
        title="Organization Phone Number (Optional)"
        errors={
          errors.phoneNumber && touched.phoneNumber ? errors.phoneNumber : ""
        }
        containerClassName="col-span-full"
      />
    </div>
  );
};

export const ProfileSettings = ({
  onEditSuccess = () => {},
}: {
  onEditSuccess?: () => void;
}) => {
  const [edit, setEdit] = useState(false);
  const [orgData, setOrgData] = useState<OrganizationData>();
  const [isOrgDataValid, setIsOrgDataValid] = useState(false);

  const { profile } = useAppSelector((state) => state.auth);

  const [updateProfile, { isLoading: updatingProfile }] =
    useUpdateProfileMutation();

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleSubmit,
    setFieldValue,
    handleChange,
    resetForm,
    isValid,
  } = useFormik({
    initialValues: {
      firstName: profile?.firstName ?? "",
      lastName: profile?.lastName ?? "",
      email: profile?.email ?? "",
      isOrganization: !!profile?.isOrganisation,
    },
    enableReinitialize: true,
    validationSchema: SettingPageUpdateProfileSchema,
    onSubmit: async (vals) => {
      let mainOrgData = undefined;

      if (vals.isOrganization && orgData) {
        const { isOrganisation, ...other } = orgData;
        mainOrgData = other;
      }

      const resp = await updateProfile({
        firstName: vals.firstName,
        lastName: vals.lastName,
        isOrganisation: vals.isOrganization,
        organisation: mainOrgData
          ? {
              ...mainOrgData,
              logo: mainOrgData?.logo === "" ? undefined : mainOrgData?.logo,
              phoneNumber:
                mainOrgData?.phoneNumber === ""
                  ? undefined
                  : mainOrgData?.phoneNumber,
            }
          : undefined,
      });

      if (resp.data?.status === "success") {
        setEdit(false);
        onEditSuccess();
      }
    },
  });

  return (
    <>
      <BgWrapper className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <ProfileIcon dimension={48} className="!p-[9px]" />

          <div className="flex-1">
            <MiniHeader
              header={`${profile?.firstName} ${profile?.lastName}`}
              subheader={`${profile?.email}`}
            />
          </div>

          <button
            className="w-fit"
            type="button"
            onClick={() => {
              setEdit(true);
            }}
          >
            <Icon
              svg="pencil"
              width={20}
              height={20}
              className="text-grey-600"
            />
          </button>
        </div>

        {edit && (
          <form className="grid animate-fade-in grid-cols-2 items-start gap-4">
            <TextInput
              type="text"
              name="firstName"
              value={values.firstName}
              placeholder="Enter first name"
              onChange={handleChange}
              onBlur={handleBlur}
              hasTitle
              title="First name"
              errors={
                errors.firstName && touched.firstName ? errors.firstName : ""
              }
            />

            <TextInput
              type="text"
              name="lastName"
              value={values.lastName}
              placeholder="Enter last name"
              onChange={handleChange}
              onBlur={handleBlur}
              hasTitle
              title="Last name"
              errors={
                errors.lastName && touched.lastName ? errors.lastName : ""
              }
            />

            <TextInput
              type="text"
              name="email"
              value={values.email}
              placeholder="Enter email"
              onChange={handleChange}
              onBlur={handleBlur}
              hasTitle
              title="Email"
              errors={errors.email && touched.email ? errors.email : ""}
              readOnly
              containerClassName="col-span-full"
            />
          </form>
        )}
      </BgWrapper>

      {!!profile?.isOrganisation && !edit && (
        <BgWrapper className="flex gap-3">
          {profile?.organisation?.logo && (
            <img
              alt="organization logo"
              src={profile?.organisation?.logo}
              className="size-[40px] overflow-hidden rounded-[4px] object-cover"
            />
          )}

          <div className="flex-1 text-sm text-grey-500">
            <p className="text-base font-bold text-primary">
              {profile?.organisation?.name}
            </p>
            <p>{profile?.organisation?.email}</p>
            <p>{profile?.organisation?.phoneNumber}</p>
          </div>

          <Pill text="ORG ADMIN" variation="purple" />
        </BgWrapper>
      )}

      {edit && (
        <BgWrapper className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <MiniHeader
                header="I am an Organization"
                subheader="Create event as an organization"
              />
            </div>

            <SwitchInput
              checked={values.isOrganization}
              onChange={() => {
                setFieldValue("isOrganization", !values.isOrganization);
              }}
            />
          </div>

          {values.isOrganization && (
            <OrganizationForm
              isOrg={values.isOrganization}
              handleDataChange={(data, isDataValid) => {
                setOrgData(data);
                setIsOrgDataValid(isDataValid);
              }}
            />
          )}

          <div className="grid grid-cols-2 items-center gap-2">
            <Button
              type="button"
              onClick={() => {
                setEdit(false);
                resetForm();
              }}
              text="Cancel"
              variation="plain-grey"
            />

            <Button
              type="button"
              onClick={() => {
                handleSubmit();
              }}
              text="Save"
              variation="primary"
              disabled={
                (values.isOrganization && !isOrgDataValid) ||
                !isValid ||
                updatingProfile
              }
              loading={updatingProfile}
            />
          </div>
        </BgWrapper>
      )}
    </>
  );
};
